import { Database, supabase } from "../api/supabase/client";
import type { GetResult } from "@supabase/postgrest-js/src/select-query-parser";
import jsonHash from "json-stable-stringify";

export function wrap<Data, Error>(supabaseResult: {
  data: NonNullable<Data> extends never ? never : Data;
  error: Error | undefined;
}) {
  if (supabaseResult.error) {
    console.warn(supabaseResult.error);
    throw supabaseResult.error;
  }

  return supabaseResult.data!;
}

// Store pending requests
const pendingRequests: {
  [key: string]: {
    table: keyof Database["public"]["Tables"];
    query: string;
    ids: Set<number>;
    resolvers: Map<number, ((data: any) => void)[]>;
    timer?: NodeJS.Timeout;
  };
} = {};

// const DEFAULT_DEBUG = process.env.NODE_ENV === "development";
const DEFAULT_DEBUG = false

export async function supabaseSelect<
  TableName extends keyof Database["public"]["Tables"],
  Query extends string
>(
  table: TableName,
  query: Query,
  id: number,
  DEBUG = DEFAULT_DEBUG
): Promise<{
  data: GetResult<
    Database["public"],
    Database["public"]["Tables"][TableName]["Row"],
    unknown,
    Database["public"]["Tables"][TableName]["Row"] extends {
      Relationships: infer R;
    }
      ? R
      : unknown,
    Query
  >[];
  error: Error | undefined;
}> {
  const result = await _supabaseSelect(table, query, id);

  if (DEBUG) {
    const expected = await supabase.from(table).select(query).match({
      id,
    });
    if (jsonHash(result) !== jsonHash(expected)) {
      console.error("NON_IDENTICAL_RESULTS", {
        table,
        query,
        result,
        expected,
      });
      debugger;
    }
  }

  return result;
}

const wrapSuccess = <T extends any[]>(data: T) => {
  return structuredClone({
    count: null,
    data,
    error: null,
    status: 200,
    statusText: "",
  });
};

/**
 * Batches Supabase select requests for the same table and query, sending them all at once after a short delay.
 * @param table - The name of the table to query.
 * @param query - The select query string.
 * @param id - The ID of the record to fetch.
 * @returns A Promise that resolves to the fetched data.
 */
export function _supabaseSelect<
  TableName extends keyof Database["public"]["Tables"],
  Query extends string
>(
  table: TableName,
  query: Query,
  id: number
): Promise<{
  data: GetResult<
    Database["public"],
    Database["public"]["Tables"][TableName]["Row"],
    unknown,
    Database["public"]["Tables"][TableName]["Row"] extends {
      Relationships: infer R;
    }
      ? R
      : unknown,
    Query
  >[];
  error: Error | undefined;
}> {
  const key = table + "|" + query;

  if (!pendingRequests[key]) {
    pendingRequests[key] = {
      table,
      query,
      ids: new Set(),
      resolvers: new Map(),
      timer: undefined,
    };
  }

  pendingRequests[key].ids.add(id);

  return new Promise((resolve, reject) => {
    // Add the resolver and rejecter to the maps
    if (!pendingRequests[key].resolvers.has(id)) {
      pendingRequests[key].resolvers.set(id, []);
    }
    pendingRequests[key].resolvers.get(id)!.push(resolve);

    // Set a timer if not already set
    if (!pendingRequests[key].timer) {
      pendingRequests[key].timer = setTimeout(() => {
        executeBatchRequest(key);
      }, 50); // Debounce delay in milliseconds
    }
  });
}

async function executeBatchRequest(key: string) {
  const { table, query, ids, resolvers } = pendingRequests[key];

  // Clear the pending request
  delete pendingRequests[key];

  const idsArray = Array.from(ids);

  try {
    const { data, error } = await supabase
      .from(table)
      .select(query)
      .in("id", idsArray);

    if (error) {
      // Reject all pending promises
      for (const id of idsArray) {
        const resolver = resolvers.get(id);
        if (resolver) {
          //Resolve with err
          resolver.forEach((resolve) => resolve({ data: undefined, error }));
        }
      }
    } else {
      // Map data by id
      const dataById = new Map<number, any>();
      if (data) {
        for (const item of data as any) {
          const itemId = item.id;
          dataById.set(itemId, item);
        }
      }

      // For each id, resolve or reject accordingly
      for (const id of idsArray) {
        const resolves = resolvers.get(id);
        if (dataById.has(id)) {
          //Need to deepcopy because sometimes we modify in place
          const itemData = dataById.get(id);
          if (resolves) {
            resolves.forEach((resolve) => resolve(wrapSuccess([itemData])));
          }
        } else {
          // No data for this id
          if (resolves) {
            resolves.forEach((resolve) => resolve(wrapSuccess([])));
          }
        }
      }
    }
  } catch (err) {
    // In case of unexpected error
    for (const id of idsArray) {
      const resolves = resolvers.get(id);
      if (resolves) {
        resolves.forEach((resolve) => resolve({ data: undefined, error: err }));
      }
    }
  }
}

// const test = async () => {
//   const xx = await supabase
//     .from("Module")
//     .select(
//       "*,TeacherAccount(StaffDetails(*)),topic:Topic(*,level:Level(*)),engagementPanels:EngagementPanel(*)"
//     )
//     .in(
//       "id",
//       [
//         3, 2, 1, 4, 5, 7, 6, 11, 8, 13, 12, 14, 16, 15, 17, 18, 19, 20, 21, 22,
//         23,
//       ]
//     );
//   debugger;
// };

// test();
